import React from "react";
import { Zap, ChevronUp, X } from "lucide-react";
import { useFastCheck } from "@/contexts/FastCheckContext";

/**
 * Renders the Fast Fact-Check sources either as an expanded inline card
 * (default) OR as a compact fixed action bar at the bottom of the
 * viewport when collapsed. State + persistence is owned by
 * `FastCheckContext` so the data survives route changes and drawer
 * toggles.
 *
 *   • `variant="inline"`  — full list, rendered inside ScanInterface.
 *                          Hidden when collapsed.
 *   • `variant="floating"` — compact bottom bar, rendered at App level.
 *                          Hidden when expanded (or when no data).
 */
const FastCheckPanel = ({ variant = "inline" }) => {
  const { results, isCollapsed, collapse, expand, clear } = useFastCheck();

  // Floating bottom bar — only renders when there's data AND collapsed.
  if (variant === "floating") {
    if (!results || !isCollapsed) return null;
    return (
      <div
        data-testid="fast-check-floating-bar"
        className="fixed inset-x-0 z-40 px-3 pointer-events-none"
        style={{ bottom: "max(12px, env(safe-area-inset-bottom))" }}
      >
        <div className="pointer-events-auto mx-auto max-w-2xl border border-amber-400/40 bg-[#020617]/95 backdrop-blur-md shadow-[0_-12px_30px_rgba(0,0,0,0.6)] flex items-center justify-between gap-2">
          <button
            type="button"
            data-testid="fast-check-expand-bar"
            onClick={expand}
            className="flex-1 inline-flex items-center justify-center gap-2 px-4 py-3 font-mono text-[11px] uppercase tracking-[0.22em] text-amber-300 hover:bg-amber-400/[0.08] transition-colors"
          >
            <ChevronUp size={13} />
            Expand Previous Fact-Check Sources (0 Credits)
            <span className="font-bold text-amber-200">
              · {results.sources?.length || 0}
            </span>
          </button>
          <button
            type="button"
            data-testid="fast-check-floating-close"
            onClick={clear}
            aria-label="Clear fast-check results"
            className="w-10 h-10 flex items-center justify-center text-[#64748b] hover:text-amber-300 transition-colors border-l border-amber-400/20"
          >
            <X size={14} />
          </button>
        </div>
      </div>
    );
  }

  // Inline expanded panel — only renders when data present AND not collapsed.
  if (!results || isCollapsed) return null;

  return (
    <section
      data-testid="fast-check-results"
      className="mt-5 border border-amber-400/30 bg-amber-400/[0.04]"
    >
      <header className="flex items-center justify-between gap-3 px-4 py-2.5 border-b border-amber-400/20">
        <div className="flex items-center gap-2">
          <Zap size={13} className="text-amber-400" />
          <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-amber-300">
            Fast Fact-Check · 0 credits deducted
          </span>
        </div>
        <button
          type="button"
          data-testid="fast-check-collapse"
          onClick={collapse}
          className="font-mono text-[10px] uppercase tracking-[0.18em] text-[#94a3b8] hover:text-amber-300 transition-colors"
          aria-label="Collapse fast-check results"
        >
          Collapse ▾
        </button>
      </header>
      <ul className="divide-y divide-white/[0.06]">
        {(results.sources || []).map((src, idx) => (
          <li
            key={src.id}
            data-testid={`fast-source-${idx}`}
            className="p-3.5 sm:p-4"
          >
            <a
              href={src.link}
              target="_blank"
              rel="noopener noreferrer"
              className="block group min-w-0"
            >
              <div className="flex items-center gap-2 mb-1.5 min-w-0">
                <span className="font-mono text-[10px] text-[#475569] tabular-nums shrink-0">
                  {String(idx + 1).padStart(2, "0")}
                </span>
                <span className="font-mono text-[9px] uppercase tracking-widest text-[#475569] group-hover:text-amber-300 transition-colors truncate min-w-0">
                  {src.source || "OPEN ↗"}
                </span>
              </div>
              <h4 className="text-sm font-bold text-[#f1f5f9] group-hover:text-amber-300 transition-colors tracking-tight mb-1 leading-snug break-words">
                {src.title}
              </h4>
              <p className="text-[12px] text-[#94a3b8] font-mono leading-relaxed break-words">
                {src.snippet}
              </p>
            </a>
          </li>
        ))}
        {results.sources?.length === 0 && (
          <li className="p-4 font-mono text-[11px] text-[#94a3b8]">
            No sources returned for that query.
          </li>
        )}
      </ul>
    </section>
  );
};

export default FastCheckPanel;
